// @flow
import * as React from 'react';
import CampaignForm from './CampaignForm';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Row, Col } from 'react-bootstrap';
import moment from 'moment';

type Props = {
  currentCampaign: Object,
  params: Object,
  router: Object,
  addCampaign: Function,
  updateCampaign: Function,
  cloneCampaign: Function,
  getInsightSetInsights: Function,
  getCampaignSettings: Function,
  getCurrentOrganisationShapeSets: Function,
  getCurrentOrganisationInsightSets: Function
};

type State = {
  loading: boolean,
  isSaving: boolean,
  isArchiving: boolean,
  successMsg?: string,
  errorMsg?: string
};

class CampaignDetails extends React.Component<Props, State> {
  timeoutId: any;

  constructor(props: Props) {
    super(props);
    this.state = {
      loading: true,
      isSaving: false,
      isArchiving: false,
      successMsg: undefined
    };
    this.timeoutId = undefined;
  }

  componentDidMount() {
    Promise.all([
      this.loadCampaignData(this.props),
      this.loadOrganisationData(this.props)
    ]).then(success => {
      this.setState({ loading: false });
    });
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  componentWillReceiveProps(nextProps: Props) {
    // if currentCampaign has changed
    const currentCampaignId = this.props.currentCampaign.id;
    const newCampaignId = nextProps.currentCampaign.id;
    if (newCampaignId !== currentCampaignId) {
      this.loadCampaignData(nextProps);
    }

    // if organisationId has changed
    const currentOrgId = this.props.params.organisationId;
    const newOrgId = nextProps.params.organisationId;
    if (newOrgId !== currentOrgId) {
      this.loadOrganisationData(nextProps);
    }
  }

  loadCampaignData = (props: Props) => {
    const {
      getInsightSetInsights,
      currentCampaign: { insightSetId },
      params: { context, campaignId, organisationId }
    } = props;
    const promisesToRun = [];

    if (campaignId === 'new') {
      promisesToRun.push(
        this.props.getCampaignSettings(parseInt(organisationId, 10))
      );
    }

    // get InsightSetInsights if InsightSetId is set
    if (insightSetId) {
      promisesToRun.push(getInsightSetInsights(insightSetId, true, context));
    }
    return Promise.all(promisesToRun);
  };

  loadOrganisationData = (props: Props) => {
    const {
      getCurrentOrganisationShapeSets,
      getCurrentOrganisationInsightSets,
      params: { organisationId }
    } = props;
    const promisesToRun = [];

    // get shapesets
    const shapeType = 1; // location
    const isAllocatedTo = true;
    const excludeArchived = true;
    const excludeMissingRefName = true;
    promisesToRun.push(
      getCurrentOrganisationShapeSets(
        organisationId,
        isAllocatedTo,
        shapeType,
        excludeArchived,
        excludeMissingRefName
      )
    );

    // get insightSets
    promisesToRun.push(getCurrentOrganisationInsightSets(organisationId));

    return Promise.all(promisesToRun);
  };

  currentOrgId = () => parseInt(this.props.params.organisationId, 10);

  success = (redirect: any, response: any, isNew: boolean = false) => {
    if (!redirect) {
      // not going to Map page
      this.setState({
        isSaving: false,
        errorMsg: undefined,
        successMsg: 'Success!'
      });
      this.timeoutId = setTimeout(
        () => this.setState({ successMsg: undefined }),
        3000
      );

      if (isNew) {
        // new campaign has been created by clicking 'Save'.
        // need to redirect to new campaign page
        const currentOrgId = this.currentOrgId();
        const context = this.props.params.context;
        const newCampaignId = response.data.id;
        this.props.router.push(
          `/${context}/${currentOrgId}/campaigns/${newCampaignId}`
        );
      }
    }
  };

  failure = (error: Object) => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  archiveCampaign = (values: any) => {
    this.setState({ isArchiving: true });
    const updatedValues = {
      ...values,
      isArchived: !values.isArchived,
      name: undefined
    };
    this.props
      .updateCampaign(updatedValues)
      .then(() => this.setState({ isArchiving: false }));
  };

  handleSubmit = (values: Object) => {
    let redirect = false;
    if (values.button === 'Map') {
      values = { ...values, statusTypeId: 2 };
      redirect = true;
    }
    values = { ...values };

    this.addOrUpdateCampaign(values, redirect);
  };

  addOrUpdateCampaign = (values: Object, redirect: boolean = false) => {
    this.setState({ isSaving: true });
    const currentOrgId = this.currentOrgId();
    const context = this.props.params.context;

    if (values.id) {
      this.props
        .updateCampaign(values, context, redirect)
        .then(
          success => this.success(redirect, success),
          error => this.failure(error)
        );
    } else {
      if (redirect) {
        // set statusTypeId to mapping
        values.statusTypeId = 2;
      }
      this.props
        .addCampaign(values, currentOrgId, context, redirect)
        .then(
          success => this.success(redirect, success, true),
          error => this.failure(error)
        );
    }
  };

  render() {
    const {
      insightSetId,
      boundaryOverlapTypeId,
      locationShapeSetId
    } = this.props.currentCampaign;
    const insightsChecked = !!insightSetId;
    const catchmentChecked = !!boundaryOverlapTypeId;
    const moreOptionsOpen =
      insightsChecked || catchmentChecked || !!locationShapeSetId;

    const initialValues = {
      ...this.props.currentCampaign,
      insightsChecked,
      catchmentChecked,
      moreOptionsOpen
    };

    if (this.state.loading) {
      return <LoadingSpinner />;
    }

    if (!this.props.isGlobal) {
      return (
        <CampaignForm
          {...this.props}
          {...this.state}
          initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={this.handleSubmit}
          archiveCampaign={this.archiveCampaign}
          cloneCampaign={this.props.cloneCampaign}
          canSendToDoNotMail={this.props.canSendToDoNotMail}
          errorMsg={this.state.errorMsg}
          successMsg={this.state.successMsg}
        />
      );
    } else {
      return (
        <div>
          <h1> Campaign Details</h1>
          {this.props.currentCampaign && this.props.currentCampaign.id && (
            <Row>
              <Col xs={6} lg={6}>
                <span className="form--label">Campaign Name</span>
                <p>{this.props.currentCampaign.name}</p>
              </Col>
              <Col xs={6} lg={6}>
                <span className="form--label">Date of Creation</span>
                <p>
                  {moment(this.props.currentCampaign.dateCreated).format(
                    'DD/MM/YYYY'
                  )}
                </p>
              </Col>
              <Col xs={6} lg={6}>
                <span className="form--label">Customer</span>
                <p>
                  <a
                    href={
                      '/customers/' +
                      (this.props.currentCampaign.organisationOwnerId || '')
                    }
                  >
                    View Customer
                  </a>
                </p>
              </Col>
              <Col xs={6} lg={6}>
                <span className="form--label">Last CHanged By</span>
                <p>{this.props.currentCampaign.lastChangedBy}</p>
              </Col>
            </Row>
          )}
          {!this.props.currentCampaign ||
            (!this.props.currentCampaign.id && <p>No Campaign Selected</p>)}
        </div>
      );
    }
  }
}

export default CampaignDetails;



// WEBPACK FOOTER //
// ./src/containers/campaigns/CampaignDetails.js